<template>
    <card title="Failed Faxes">
        <b-button @click="reloadFaxes">
            <b-icon-arrow-clockwise />
        </b-button>
        <b-row>
            <b-col>
                <b-table id="failed-fax-table" ref="failed-fax-table" api-url="/api/Fax/failed" outlined show-empty
                    no-provider-sorting no-provider-filtering :filter-included-fields="['messageId']" :filter="archived"
                    :filter-function="filterFunction" :fields="faxFields" :items="faxTableProvider">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(actions)="row">
                        <div class="d-flex" style="gap:15px;">
                            <b-button variant="outline-info" size="sm" @click="changeNumber(row)" v-b-tooltip.hover
                                title="Change Fax Number">
                                <b-icon-pencil-square />
                            </b-button>
                            <b-button variant="outline-primary" size="sm" @click="resend(row)" v-b-tooltip.hover
                                title="Resend">
                                <b-icon-arrow-repeat />
                            </b-button>
                            <b-button variant="outline-danger" size="sm" @click="archive(row)" v-b-tooltip.hover
                                title="Archive">
                                <b-icon-archive />
                            </b-button>
                        </div>
                    </template>
                    <template #cell(details)="row">
                        <i :class="detailsClass(row)" role="button" @click="selectRow(row)" />
                    </template>
                    <template #row-details="row">
                        <b-card>
                            <div :id="`pdf-viewer-${row.item.messageId}`" v-b-visible="imageSource(row)" />
                        </b-card>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-modal id="fax-number-change-modal" size="sm" title="Fax Number">
            <b-input v-model="selectedFax.faxNumber" />
        </b-modal>
    </card>
</template>
<script lang="ts">
import { Component, Ref, Vue } from "vue-property-decorator";
import { BTable, BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import moment from "moment/moment";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PDFObject from "pdfobject";
import TextUtils from "@/util/TextUtils";

@Component({
    name: "FailedFaxComponent",
})
export default class FailedFaxComponent extends Vue {
    @Ref("failed-fax-table") faxTable!: BTable;
    archived: string[] = [];

    private hasCustomFields: boolean = false;

    get faxFields(): BvTableFieldArray {
        return [
            this.hasCustomFields ? {
                key: "cf.faxDocumentType",
                label: "Fax Type",
                sortable: true,
            } : "",
            { key: "messageId" },
            {
                key: "faxNumber",
                sortable: true,
            },
            { key: "resultInfo" },
            { key: "pagesCount" },
            {
                key: "errorCode",
                sortable: true,
            },
            { key: "resultCode" },
            {
                key: "createdAt",
                formatter: (d: any) => moment(d).format("L LT"),
                sortable: true,
            },
            {
                key: "resolvedDate",
                formatter: (d: any) => moment(d).format("L LT"),
                sortable: true,
            },
            {
                key: "actions",
                class: "text-center",
            },
            {
                key: "details",
                class: "text-center",
            },

        ];
    }

    detailsClass(r: any) {
        return `text-primary fa fa-eye${r.detailsShowing ? '-slash' : ''}`;
    }

    reloadFaxes() {
        this.faxTable.refresh();
    }

    filterFunction(item: any, filt: any[]): boolean {
        const messageId = item.messageId;
        return !filt.find(v => v == messageId);
    }

    selectedFax: any = {};

    async changeNumber(row: any) {
        this.selectedFax = row.item;
        this.$bvModal.show("fax-number-change-modal");
    }

    async resend(row: any) {
        const messageId = row.item.messageId;
        this.archived.push(messageId);
        try {
            const faxNumber = TextUtils.formatFaxNumber(row.item.faxNumber);
            await axios.post(`/api/Fax/resend/${row.item.messageId}`, { faxNumber: faxNumber });
            this.$notification(NotificationOptions.notificationOptionsPreset("Successfully re-sent",
                NotificationOptions.NotificationTypes.success));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.archived.pop();
        }
    }

    async archive(row: any) {
        const messageId = row.item.messageId;
        this.archived.push(messageId);
        try {
            await axios.post(`/api/Fax/archive/${messageId}`);
            this.$notification(NotificationOptions.notificationOptionsPreset("Successfully archived",
                NotificationOptions.NotificationTypes.success));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.archived.pop();
        }
    }

    selectRow(r: any) {
        r.toggleDetails();
    }

    imageSource(row: any) {
        const imageSource = `${axios.defaults.baseURL}/fax/image/${row.item.messageId}`;
        axios.get(imageSource, { responseType: "blob" })
            .then(r => {
                const objectUrl = URL.createObjectURL(r.data);
                const options = {
                    pdfOpenParams: {
                        view: 'FitW',
                        pagemode: 'thumbs',
                        toolbar: 1,
                        scrollbar: 0,
                    },
                };
                PDFObject.embed(objectUrl, `#pdf-viewer-${row.item.messageId}`, options);
            }).catch(err => this.$notification(NotificationOptions.error(err)));

        return imageSource;
    }

    async faxTableProvider(ctx: BvTableCtxObject): Promise<any[]> {
        const uri = ctx.apiUrl + "";
        try {
            this.hasCustomFields = false;
            const resp = await axios.get<any[]>(uri);
            const faxes = resp.data as any[];

            if (faxes.some(f => f.cf != undefined && f.cf.faxDocumentType != undefined)) {
                this.hasCustomFields = true;
            }

            return faxes;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            return [];
        }
    }
};
</script>
